/*
  What a press on the asset table has turned into: a sideways pan of the
  scroller, a reorder of the row under it, or neither yet.

  Kept apart from the component because the whole gesture turns on this one
  comparison, and on the number below — which was off by a single pixel, and
  made dragging the table sideways all but impossible.

  ---- what was measured, and what it means ----

  Chrome was driven through a press with the DevTools protocol, one pixel at a
  time, on a draggable row. The order of events is:

      move@101   move@102   move@103   DRAGSTART@100   move@104 ...

  Two things fall out of that, and both are load-bearing.

  FIRST: the browser opens its native drag on the third pixel, and the move for
  that third pixel is delivered just BEFORE it. So there is a window — one, two,
  three — in which a pan can claim the press, and it shuts at three. The old
  threshold was four. It could only ever be reached by move@104, which arrives
  after the row has already been picked up, so on a row the pan never won. The
  single place it did work was the 2px of `border-spacing` BETWEEN two rows,
  where there is no draggable element and so no drag to lose to — which is
  exactly how it was reported: you had to hit the line between the rows.

  SECOND, and this is why the old code could not be rescued by lowering the
  number alone: `DRAGSTART@100` reports x=100, the position the press went DOWN
  at, not where the pointer had reached. The travel read from a `dragstart` is
  therefore always exactly zero, in both axes. The comment that used to sit here
  said the decision was made at `dragstart` "from how far the press has
  travelled since it went down" — there is no such distance to read. That path
  could never decide anything, and has been removed rather than tuned.

  (A fast flick — one large jump in a single move — fires no `dragstart` at all,
  which is why panning sometimes appeared to work.)
*/

/*
  Three, because the move for the third pixel is the last one delivered before
  the browser commits. Chrome has the shortest drag threshold of the browsers
  here, so a rule that wins in Chrome wins everywhere.
*/
export const PAN_MIN_TRAVEL = 3;

/*
  `dx`/`dy` are the travel since the press went down.

  "pan" only where the horizontal beats the vertical outright. A tie goes to the
  row: reordering is the deliberate act and scrolling the casual one, so a
  gesture that cannot say which way it is going must not steal the reorder.
*/
export const gestureFrom = (dx, dy, minTravel = PAN_MIN_TRAVEL) => {
  const ax = Math.abs(dx);
  const ay = Math.abs(dy);
  if (ax < minTravel && ay < minTravel) return "undecided";
  return ax > ay ? "pan" : "drag";
};
